import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { FromSettings } from "@/features/settings/components/FromSettings"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { CalendarIcon } from "lucide-react"
import { useTemplateSettings } from "../../../../api/templateSettingsApi"
import { intlLocale } from "../../../../utils/date"
import { SectionLabel } from "../../../shared/SectionLabel"
import type { DraftSettingsTarget } from "./DraftSettingsSheet"
import {
  formatInquiryDue,
  parseIsoDate,
  toIsoDateString,
} from "./inquiryDraftMock"

interface DeadlineDeliverySectionProps {
  // Stored as a stable "YYYY-MM-DD" calendar date — see inquiryDraftMock.ts.
  due: string
  setDue: (next: string) => void
  // Opens the reminder settings beside the draft (the page's side sheet).
  onOpenSettings?: (target: DraftSettingsTarget) => void
}

export function DeadlineDeliverySection({
  due,
  setDue,
  onOpenSettings,
}: DeadlineDeliverySectionProps) {
  const { translate, currentLanguage } = useTranslation()
  const selected = due ? parseIsoDate(due) : undefined
  // Local midnight, matching parseIsoDate's basis, so "today" itself stays selectable.
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  // Show the org's actual reminder cadence (from settings) rather than a vague sentence,
  // so the user knows exactly what happens after the deadline.
  const { data: settings } = useTemplateSettings()

  return (
    <div className="flex flex-col gap-2">
      <SectionLabel>{translate("MC_INQ_DRAFT_SEC_DEADLINE")}</SectionLabel>
      <div className="rounded-lg border border-grey4 bg-grey2 px-4 py-1">
        <div className="flex items-center gap-3.5 py-3">
          <span className="w-24 shrink-0 text-[11.5px] text-grey5">
            {translate("MC_INQ_DRAFT_DEADLINE_DUE")}
          </span>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex h-9 w-44 items-center gap-2 rounded-md border border-grey4 bg-grey3 px-3 text-left text-[13px] text-white outline-hidden focus-visible:border-yellow1"
              >
                <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-grey5" />
                {selected ? (
                  <span>
                    {formatInquiryDue(due, intlLocale(currentLanguage))}
                  </span>
                ) : (
                  <span className="text-grey5">
                    {translate("MC_INQ_DRAFT_DEADLINE_PICK_DATE")}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selected}
                // A deadline in the past would go out already expired and fire the
                // first auto-reminder immediately.
                disabled={{ before: today }}
                onSelect={(d) => {
                  if (d) setDue(toIsoDateString(d))
                }}
              />
            </PopoverContent>
          </Popover>
          {selected && (
            <span className="text-[11.5px] text-grey5">
              {translate("MC_INQ_DRAFT_DEADLINE_FILLS")}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3.5 border-t border-white/5 py-3">
          <span className="flex w-24 shrink-0 items-center gap-1 text-[11.5px] text-grey5">
            {translate("MC_INQ_DRAFT_DEADLINE_REMINDERS")}
          </span>
          <span className="flex flex-col items-start gap-1">
            <span className="text-[11.5px] leading-snug text-grey5">
              {settings && !settings.remindersEnabled
                ? translate("MC_INQ_DRAFT_DEADLINE_REMINDERS_OFF")
                : settings
                  ? translate("MC_INQ_DRAFT_DEADLINE_REMINDERS_VALUE", {
                      interval: settings.reminderIntervalDays,
                      max: settings.maxReminders,
                    })
                  : translate("MC_INQ_DRAFT_DEADLINE_REMINDERS_LOADING")}
            </span>
            <FromSettings
              target="inquiry.reminders"
              onOpen={
                onOpenSettings && (() => onOpenSettings("inquiry.reminders"))
              }
            />
          </span>
        </div>
      </div>
    </div>
  )
}
